import { useEffect } from 'react';
import { Modal } from '@mui/material';
import { useForm } from 'react-hook-form';
import useAddressValidation from '../../../../hooks/useAddressValidation';
import AddressValidationDialog from '../../../../components/AddressValidationDialog';

// Format phone number as (999) 999-9999 for display
const formatPhone = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

// Account status options
const accountStatusOptions = [
  { value: 'pending', text: 'Pending' },
  { value: 'active', text: 'Active' },
  { value: 'closed', text: 'Closed' },
];

const AccountModal = ({
  isOpen,
  onClose,
  onSubmit,
  isEditMode,
  defaultValues,
  isSubmitting,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      client_code: '',
      description: '',
      first_name: '',
      middle_name: '',
      last_name: '',
      email: '',
      phone: '',
      mobile: '',
      business_name: '',
      signup_date: '',
      cancel_date: '',
      account_status: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      activated_date: '',
      activated_by: '',
      total_licenses: '',
      active_licenses: '',
      total_users: '',
      active_users: '',
      inactive_users: '',
    },
  });

  const {
    validateAndProceed, isValidating, dialogState,
    acceptSuggestion, keepOriginal, saveAnyway, cancelDialog,
  } = useAddressValidation({
    fieldMap: { street: 'address1', street2: 'address2' },
  });

  const handleValidatedSubmit = (data) => {
    validateAndProceed(data, (validatedData) => {
      // Auto-fill form fields from USPS response
      if (validatedData.city !== data.city) setValue('city', validatedData.city);
      if (validatedData.state !== data.state) setValue('state', validatedData.state);
      if (!validatedData.country?.trim()) {
        validatedData.country = 'US';
      }
      // Block save if city or state is still empty
      if (validatedData.address1?.trim() && validatedData.zip?.trim()) {
        if (!validatedData.city?.trim() || !validatedData.state?.trim()) {
          return;
        }
      }
      onSubmit(validatedData);
    });
  };

  // Reset form when modal opens or defaultValues change
  useEffect(() => {
    if (isOpen && defaultValues) {
      // Format phone numbers for display
      reset({
        ...defaultValues,
        phone: formatPhone(defaultValues.phone),
        mobile: formatPhone(defaultValues.mobile),
      });
    }
  }, [isOpen, defaultValues, reset]);

  // Handle phone input formatting
  const handlePhoneChange = (e, fieldName) => {
    const formatted = formatPhone(e.target.value);
    setValue(fieldName, formatted);
  };

  const handleClose = () => {
    reset({
      client_code: '',
      description: '',
      first_name: '',
      middle_name: '',
      last_name: '',
      email: '',
      phone: '',
      mobile: '',
      business_name: '',
      signup_date: '',
      cancel_date: '',
      account_status: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      activated_date: '',
      activated_by: '',
      total_licenses: '',
      active_licenses: '',
      total_users: '',
      active_users: '',
      inactive_users: '',
    });
    onClose();
  };

  return (
    <>
    <Modal open={isOpen} onClose={handleClose}>
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 w-full md:w-[1100px] max-w-6xl max-h-[90vh] overflow-y-auto border border-gray-200 relative">
          {/* Modal Title and Close Button */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-secondary-800">
              {isEditMode
                ? `Edit > ${defaultValues?.business_name || `${defaultValues?.first_name || ''} ${defaultValues?.last_name || ''}`.trim() || 'Client'}`
                : 'Add Account'}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-500 hover:text-secondary-800 text-2xl font-bold px-2"
              aria-label="Close"
            >
              &times;
            </button>
          </div>

          <form
            className="flex flex-col gap-4 md:gap-6 text-text-hint"
            onSubmit={handleSubmit(handleValidatedSubmit)}
          >
            {/* Row 1: Client Code, Business Name, Email */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5">
              <div className="flex flex-col space-y-1">
                <label htmlFor="client_code" className="text-text-hint mb-1">
                  Client Code <span className="text-danger-500">*</span>
                </label>
                <input
                  {...register('client_code', {
                    required: 'Client Code is required',
                    maxLength: { value: 15, message: 'Max 15 characters' },
                  })}
                  type="text"
                  placeholder="Client Code"
                  id="client_code"
                  disabled={isEditMode}
                  className={`outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black ${
                    isEditMode ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''
                  }`}
                />
                {errors.client_code && (
                  <span className="text-red-500 text-xs">{errors.client_code.message}</span>
                )}
              </div>

              <div className="flex flex-col space-y-1">
                <label htmlFor="business_name" className="text-text-hint mb-1">
                  Business Name
                </label>
                <input
                  {...register('business_name', {
                    maxLength: { value: 50, message: 'Max 50 characters' },
                  })}
                  type="text"
                  placeholder="Business Name"
                  id="business_name"
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
                />
                {errors.business_name && (
                  <span className="text-red-500 text-xs">{errors.business_name.message}</span>
                )}
              </div>

              <div className="flex flex-col space-y-1">
                <label htmlFor="email" className="text-text-hint mb-1">
                  Email
                </label>
                <input
                  {...register('email', {
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Please enter a valid email address',
                    },
                    maxLength: { value: 50, message: 'Max 50 characters' },
                  })}
                  type="email"
                  placeholder="youremail@gmail.com"
                  id="email"
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
                />
                {errors.email && (
                  <span className="text-red-500 text-xs">{errors.email.message}</span>
                )}
              </div>
            </div>

            {/* Row 2: Phone, Mobile, Description */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5">
              <div className="flex flex-col space-y-1">
                <label htmlFor="phone" className="text-text-hint mb-1">
                  Phone
                </label>
                <input
                  {...register('phone')}
                  type="text"
                  placeholder="(999) 999-9999"
                  id="phone"
                  onChange={(e) => handlePhoneChange(e, 'phone')}
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
                />
              </div>

              <div className="flex flex-col space-y-1">
                <label htmlFor="mobile" className="text-text-hint mb-1">
                  Mobile
                </label>
                <input
                  {...register('mobile')}
                  type="text"
                  placeholder="(999) 999-9999"
                  id="mobile"
                  onChange={(e) => handlePhoneChange(e, 'mobile')}
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
                />
              </div>

              <div className="flex flex-col space-y-1">
                <label htmlFor="description" className="text-text-hint mb-1">
                  Description
                </label>
                <input
                  {...register('description', {
                    maxLength: { value: 25, message: 'Max 25 characters' },
                  })}
                  type="text"
                  placeholder="Description"
                  id="description"
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
                />
                {errors.description && (
                  <span className="text-red-500 text-xs">{errors.description.message}</span>
                )}
              </div>
            </div>

            {/* Row 3: First Name, Middle Name, Last Name */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5">
              <div className="flex flex-col space-y-1">
                <label htmlFor="first_name" className="text-text-hint mb-1">
                  First Name <span className="text-danger-500">*</span>
                </label>
                <input
                  {...register('first_name', {
                    required: 'First Name is required',
                    maxLength: { value: 50, message: 'Max 50 characters' },
                  })}
                  type="text"
                  placeholder="First Name"
                  id="first_name"
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
                />
                {errors.first_name && (
                  <span className="text-red-500 text-xs">{errors.first_name.message}</span>
                )}
              </div>

              <div className="flex flex-col space-y-1">
                <label htmlFor="middle_name" className="text-text-hint mb-1">
                  Middle Name
                </label>
                <input
                  {...register('middle_name', {
                    maxLength: { value: 50, message: 'Max 50 characters' },
                  })}
                  type="text"
                  placeholder="Middle Name"
                  id="middle_name"
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
                />
                {errors.middle_name && (
                  <span className="text-red-500 text-xs">{errors.middle_name.message}</span>
                )}
              </div>

              <div className="flex flex-col space-y-1">
                <label htmlFor="last_name" className="text-text-hint mb-1">
                  Last Name <span className="text-danger-500">*</span>
                </label>
                <input
                  {...register('last_name', {
                    required: 'Last Name is required',
                    maxLength: { value: 50, message: 'Max 50 characters' },
                  })}
                  type="text"
                  placeholder="Last Name"
                  id="last_name"
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
                />
                {errors.last_name && (
                  <span className="text-red-500 text-xs">{errors.last_name.message}</span>
                )}
              </div>
            </div>

            {/* Row 4: Address Line 1, Address Line 2, Zip */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5">
              <div className="flex flex-col space-y-1">
                <label htmlFor="address1" className="text-text-hint mb-1">
                  Address Line 1
                </label>
                <input
                  {...register('address1', {
                    maxLength: { value: 50, message: 'Max 50 characters' },
                  })}
                  type="text"
                  placeholder="Address Line 1"
                  id="address1"
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
                />
              </div>

              <div className="flex flex-col space-y-1">
                <label htmlFor="address2" className="text-text-hint mb-1">
                  Address Line 2
                </label>
                <input
                  {...register('address2', {
                    maxLength: { value: 50, message: 'Max 50 characters' },
                  })}
                  type="text"
                  placeholder="Address Line 2"
                  id="address2"
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
                />
              </div>

              <div className="flex flex-col space-y-1">
                <label htmlFor="zip" className="text-text-hint mb-1">
                  Zip Code
                </label>
                <input
                  {...register('zip', {
                    maxLength: { value: 15, message: 'Max 15 characters' },
                  })}
                  type="text"
                  placeholder="Zip Code"
                  id="zip"
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
                />
              </div>
            </div>

            {/* Row 5: City, State */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5">
              <div className="flex flex-col space-y-1">
                <label htmlFor="city" className="text-text-hint mb-1">
                  City
                </label>
                <input
                  {...register('city', {
                    maxLength: { value: 25, message: 'Max 25 characters' },
                  })}
                  type="text"
                  placeholder="City"
                  id="city"
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
                />
              </div>

              <div className="flex flex-col space-y-1">
                <label htmlFor="state" className="text-text-hint mb-1">
                  State
                </label>
                <input
                  {...register('state', {
                    maxLength: { value: 25, message: 'Max 25 characters' },
                  })}
                  type="text"
                  placeholder="State"
                  id="state"
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
                />
              </div>
            </div>

            {/* Row 6: Account Status, Activated Date, Activated By */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5">
              <div className="flex flex-col space-y-1">
                <label className="text-text-hint mb-1">Account Status</label>
                <ul className="grid grid-cols-3">
                  {accountStatusOptions.map((option, index) => (
                    <li key={option.value} className="relative">
                      <input
                        {...register('account_status')}
                        className="sr-only peer"
                        type="radio"
                        value={option.value}
                        id={`account_status_${option.value}`}
                      />
                      <label
                        className={`flex justify-center items-center py-2 px-3 bg-white border border-secondary-800 cursor-pointer focus:outline-none hover:bg-gray-50 peer-checked:bg-secondary-500 peer-checked:text-white peer-checked:border-secondary-800 text-sm ${
                          index === 0
                            ? 'rounded-tl-full rounded-bl-full'
                            : index === 2
                            ? 'rounded-tr-full rounded-br-full'
                            : 'border-l-0 border-r-0'
                        }`}
                        htmlFor={`account_status_${option.value}`}
                      >
                        {option.text}
                      </label>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-col space-y-1">
                <label htmlFor="activated_date" className="text-text-hint mb-1">
                  Activated Date
                </label>
                <input
                  {...register('activated_date')}
                  type="date"
                  id="activated_date"
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
                />
              </div>

              <div className="flex flex-col space-y-1">
                <label htmlFor="activated_by" className="text-text-hint mb-1">
                  Activated By
                </label>
                <input
                  {...register('activated_by', {
                    maxLength: { value: 25, message: 'Max 25 characters' },
                  })}
                  type="text"
                  placeholder="Activated By"
                  id="activated_by"
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
                />
              </div>
            </div>

            {/* Row 7: Total Licenses, Active Licenses */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5">
              <div className="flex flex-col space-y-1">
                <label htmlFor="total_licenses" className="text-text-hint mb-1">
                  Total Licenses
                </label>
                <input
                  {...register('total_licenses')}
                  type="number"
                  placeholder="Total Licenses"
                  id="total_licenses"
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
                />
              </div>

              <div className="flex flex-col space-y-1">
                <label htmlFor="active_licenses" className="text-text-hint mb-1">
                  Active Licenses
                </label>
                <input
                  {...register('active_licenses')}
                  type="number"
                  placeholder="Active Licenses"
                  id="active_licenses"
                  readOnly
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black bg-gray-100 cursor-not-allowed text-gray-500"
                />
              </div>
            </div>

            {/* Row 8: Total Users, Active Users, Inactive Users */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5">
              <div className="flex flex-col space-y-1">
                <label htmlFor="total_users" className="text-text-hint mb-1">
                  Total Users
                </label>
                <input
                  {...register('total_users')}
                  type="number"
                  placeholder="Total Users"
                  id="total_users"
                  readOnly
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black bg-gray-100 cursor-not-allowed text-gray-500"
                />
              </div>

              <div className="flex flex-col space-y-1">
                <label htmlFor="active_users" className="text-text-hint mb-1">
                  Active Users
                </label>
                <input
                  {...register('active_users')}
                  type="number"
                  placeholder="Active Users"
                  id="active_users"
                  readOnly
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black bg-gray-100 cursor-not-allowed text-gray-500"
                />
              </div>

              <div className="flex flex-col space-y-1">
                <label htmlFor="inactive_users" className="text-text-hint mb-1">
                  Inactive Users
                </label>
                <input
                  {...register('inactive_users')}
                  type="number"
                  placeholder="Inactive Users"
                  id="inactive_users"
                  readOnly
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black bg-gray-100 cursor-not-allowed text-gray-500"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4">
              <div className="flex flex-row-reverse justify-between items-center gap-3 w-full">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-secondary-700 text-text-light py-2 px-6 rounded-lg shadow hover:bg-secondary-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting
                    ? isEditMode
                      ? 'Updating...'
                      : 'Saving...'
                    : isEditMode
                    ? 'Update'
                    : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="border border-secondary-800 text-secondary-800 py-2 px-6 rounded-lg hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>

    <AddressValidationDialog
      dialogState={dialogState}
      onAcceptSuggestion={acceptSuggestion}
      onKeepOriginal={keepOriginal}
      onSaveAnyway={saveAnyway}
      onCancel={cancelDialog}
    />
    </>
  );
};

export default AccountModal;
